import {useGetParticipationQuery} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import {GetSaleVehicleApiResponse} from '@dms/api/vehicle';
import {usePermissions, usePricePermissions} from '@dms/shared';

interface GetVehicleHeaderPermissionsProps {
  vehicleId: string;
  saleVehicle?: GetSaleVehicleApiResponse;
}

export function useGetVehicleHeaderPermissions(props: GetVehicleHeaderPermissionsProps) {
  // since default vehicle (without saleVehicle) doesn't have branch, we skip branchId scope check
  const saleVehicleBranchId = !props.saleVehicle ? 'SKIP' : props.saleVehicle?.branchId;

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: props.vehicleId,
  });

  const [
    hasVehicleTagReadPermission,
    hasVehicleTagCreatePermission,
    hasVehicleTagUpdatePermission,
    hasVehicleTagDeletePermission,
    hasVehicleTagAssignPermission,
    hasVehicleTagUnassignPermission,
    canReadBusinessCase,
    canChangeVehicleOwner,
    canAssignVehicleUser,
    canUnassignVehicleUser,
  ] = usePermissions({
    permissionKeys: [
      'vehicleTagRead',
      'vehicleTagCreate',
      'vehicleTagUpdate',
      'vehicleTagDelete',
      'vehicleTagAssign',
      'vehicleTagUnassign',
      'businessCaseRead',
      'vehicleChangeOwner',
      'vehicleAssignUser',
      'vehicleUnassignUser',
    ],
    scopes: {
      vehicleTagAssign: {
        participation: vehicleParticipation,
        branchId: saleVehicleBranchId,
      },
      vehicleTagUnassign: {
        participation: vehicleParticipation,
        branchId: saleVehicleBranchId,
      },
      vehicleTagRead: {
        participation: vehicleParticipation,
        branchId: saleVehicleBranchId,
      },
      vehicleChangeOwner: {
        participation: vehicleParticipation,
        branchId: saleVehicleBranchId,
      },
      vehicleAssignUser: {
        participation: vehicleParticipation,
        branchId: saleVehicleBranchId,
      },
      vehicleUnassignUser: {
        participation: vehicleParticipation,
        branchId: saleVehicleBranchId,
      },
    },
  });

  const {canReadVehicleSellingPrice} = usePricePermissions({
    vehicleRecordId: props.vehicleId,
    businessCaseRecordId: null,
  });

  const canSeeVehiclePrice = canReadVehicleSellingPrice && canReadBusinessCase;

  return {
    hasVehicleTagReadPermission,
    hasVehicleTagCreatePermission,
    hasVehicleTagUpdatePermission,
    hasVehicleTagDeletePermission,
    hasVehicleTagAssignPermission,
    hasVehicleTagUnassignPermission,
    canReadBusinessCase,
    canChangeVehicleOwner,
    canAssignVehicleUser,
    canUnassignVehicleUser,
    canSeeVehiclePrice,
  };
}
